import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { ReactElement, useMemo } from "react";
import {
	YearlyCustomRecurrenceKind,
	YearlyCustomRecurrenceOptionsModel
} from "../../common";
import { localize } from "client_legacy/ndaw/culture";
import { IValueInputAttachable } from "client/Forms/attachable";
import { resolveFormattedTextString } from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	getDayOfMonth,
	getDayOfWeek,
	getFullMonthName
} from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { AppointmentWeekIndex } from "client/Api/Calendar/calendar";
import { formatDayOfWeek } from "client/DateAndTime/culture";
import {
	getWeekIndexInMonth,
	weekIndexLocalizationKeyMap
} from "./RecurrencePatternKindSelector";

export type YearlyCustomOptionSelectorProps = {
	handler: IValueInputAttachable<YearlyCustomRecurrenceOptionsModel>;
	startDate: Date;
	timezone: TimeZone;
};

export const YearlyCustomOptionSelector = (props: YearlyCustomOptionSelectorProps): ReactElement => {
	const {
		handler,
		startDate,
		timezone
	} = props;

	const options = useMemo(
		(): readonly SelectItem<YearlyCustomRecurrenceKind>[] => getOptions(
			startDate,
			timezone
		),
		[startDate, timezone]
	);

	const repeatOnHandler: IValueInputAttachable<YearlyCustomRecurrenceKind> = handler.for("kind");

	return (
		<FormItem
			label={localize("_Repeat")}
		>
			<Select
				{...repeatOnHandler.attachToSelect()}
				options={options}
				menuTitle={localize("_Repeat")}
				areEqual={deepComparison}

			/>
			<ValidationError {...repeatOnHandler.attachToValidationError()} />
		</FormItem>
	);
};
YearlyCustomOptionSelector.displayName="YearlyCustomOptionSelector";

function getOptions(
	startDate: Date,
	timezone: TimeZone
): readonly SelectItem<YearlyCustomRecurrenceKind>[] {
	const dayOfMonth: number = getDayOfMonth(startDate, timezone);
	const appointmentWeekIndex: AppointmentWeekIndex = getWeekIndexInMonth(startDate, timezone);
	const monthName: string = getFullMonthName(startDate, timezone);
	const formattedDayOfWeekName: string = formatDayOfWeek(
		getDayOfWeek(startDate, timezone),
		"full"
	);

	return [
		{
			text: resolveFormattedTextString({
				template: localize("_OnDayMonth"),
				replacements: [
					{
						what: "{{DayNumber}}",
						filler: dayOfMonth.toString()
					},
					{
						what: "{{MonthName}}",
						filler: getFullMonthName(startDate, timezone)
					}
				]
			}),
			value: "absolute"
		},
		{
			text: resolveFormattedTextString({
				template: localize("_OnWeekIndexAndDayOfMonth"),
				replacements: [
					{
						what: "{{WeekIndex}}",
						filler: localize(
							weekIndexLocalizationKeyMap[appointmentWeekIndex]
						)
					},
					{
						what: "{{DayName}}",
						filler: formattedDayOfWeekName
					},
					{
						what: "{{MonthName}}",
						filler: monthName
					}
				]
			}),
			value: "relative"
		}
	];
}
